import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  ActivityIndicator,
  LayoutChangeEvent,
} from 'react-native';
import { PRIMITIVE_COLORS, SEMANTIC_COLORS } from '@/constants/residents/residentColors';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { RNButton } from '@/components/common/residents/desktop/Button';
import { DesktopWorkRequestCard } from './DesktopWorkRequestCard';
import { IMaintenanceListResponse } from '@/store/api/maintenanceApi';

const CONTENT_PADDING = 28;
const CARD_GAP = 24;
const MIN_CARD_WIDTH = 280;

interface DesktopMaintenanceListViewProps {
  tickets: IMaintenanceListResponse[];
  loading: boolean;
  error: string | null;
  onNewRequest: () => void;
  onTicketPress: (ticket: IMaintenanceListResponse) => void;
}

/**
 * DesktopMaintenanceListView - Grid of work request cards with action header
 * Matches Figma design with "Something broken?" action card and work request cards grid
 */
export const DesktopMaintenanceListView: React.FC<DesktopMaintenanceListViewProps> = ({
  tickets,
  loading,
  error,
  onNewRequest,
  onTicketPress,
}) => {
  const [containerWidth, setContainerWidth] = useState(0);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    const measuredWidth = event.nativeEvent.layout.width;
    setContainerWidth(measuredWidth);
  }, []);

  const innerWidth = containerWidth - CONTENT_PADDING * 2;
  const numColumns = innerWidth >= MIN_CARD_WIDTH * 3 + CARD_GAP * 2 ? 3 : 2;
  const cardWidth = containerWidth > 0
    ? Math.floor((innerWidth - CARD_GAP * (numColumns - 1)) / numColumns)
    : 0;

  return (
    <ScrollView 
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      onLayout={handleLayout}
    >
      {/* Action Card - "Something broken?" */}
      <View style={styles.actionCard}>
        <View style={styles.actionContent}>
          {/* Wrench Icon */}
          <View style={styles.iconContainer}>
            <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={68} height={68} />
          </View>
          
          {/* Text Content */}
          <View style={styles.textContent}>
            <Text style={styles.actionTitle}>Something broken?</Text>
            <Text style={styles.actionSubtitle}>
              Submit a new request with a brief description of the problem,{'\n'}
              and we'll take care of the rest.
            </Text>
          </View>
        </View>

        {/* New Request Button */}
        <View style={styles.buttonContainer}>
          <RNButton
            title="New request"
            onPress={onNewRequest}
            variant="tertiary"
            style={styles.newRequestButton}
            textStyle={styles.newRequestButtonText}
          />
        </View>
      </View>

      {/* Loading State */}
      {loading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      )}

      {/* Error State */}
      {error && !loading && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {/* Work Request Cards Grid */}
      {!loading && !error && tickets.length > 0 && (
        <View style={styles.cardsGrid}>
          {tickets.map((ticket) => (
            <DesktopWorkRequestCard
              key={ticket.maintenanceRequestId}
              ticket={ticket}
              onPress={() => onTicketPress(ticket)}
              width={cardWidth}
            />
          ))}
        </View>
      )}

      {/* Empty State */}
      {!loading && !error && tickets.length === 0 && (
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No maintenance requests yet</Text>
          <Text style={styles.emptySubtext}>
            When you submit a maintenance request, it will appear here.
          </Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    padding: CONTENT_PADDING,
    paddingBottom: 60,
    gap: 32,
    flexGrow: 1,
  },

  // Action Card
  actionCard: {
    backgroundColor: PRIMITIVE_COLORS.gray900,
    borderRadius: 16,
    padding: 24,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  actionContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
    flex: 1,
  },
  iconContainer: {
    width: 68,
    height: 68,
  },
  textContent: {
    flex: 1,
    gap: 2,
  },
  actionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    lineHeight: 27,
  },
  actionSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: PRIMITIVE_COLORS.gray400,
    lineHeight: 21,
  },
  buttonContainer: {
    alignItems: 'flex-end',
  },
  newRequestButton: {
    backgroundColor: PRIMITIVE_COLORS.resioPink,
    height: 34,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newRequestButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: PRIMITIVE_COLORS.gray950,
    lineHeight: 18,
  },

  // Cards Grid
  cardsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    ...(Platform.OS === 'web' && ({
      display: 'flex',
    } as any)),
  },

  // Loading
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },

  // Error
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  errorText: {
    fontSize: 14,
    color: PRIMITIVE_COLORS.resioDangerRed,
    textAlign: 'center',
  },

  // Empty State
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    gap: 8,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
    textAlign: 'center',
  },
  emptySubtext: {
    fontSize: 14,
    color: PRIMITIVE_COLORS.gray400,
    textAlign: 'center',
  },
});

export default DesktopMaintenanceListView;
